import { useMemo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { Image } from "antd";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { visit } from "unist-util-visit";
import { IWebSearch } from "logic/src/objects/chat/types";
import styles from "./index.module.scss";
import "katex/dist/katex.min.css";
import CodeBlock from "./components/CodeBlock";
import TimeStamp from "./components/TimeStamp";
import MyRef from "./components/MyRef";
import { animatedPlugin } from "./animated";
import { gfmTableFromMarkdown, gfmTableToMarkdown } from "mdast-util-gfm-table";
import { gfm } from "micromark-extension-gfm";

// function remarkGfmTable() {
//   // @ts-expect-error: TS is wrong about `this`.
//   // eslint-disable-next-line unicorn/no-this-assignment
//   const self = /** @type {Processor<Root>} */ this;
//   const settings = {};
//   const data = self.data();
//   const micromarkExtensions =
//     data.micromarkExtensions || (data.micromarkExtensions = []);
//   const fromMarkdownExtensions =
//     data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);
//   const toMarkdownExtensions =
//     data.toMarkdownExtensions || (data.toMarkdownExtensions = []);
//   micromarkExtensions.push(gfm(settings));
//   fromMarkdownExtensions.push([gfmTableFromMarkdown()]);
//   toMarkdownExtensions.push({
//     extensions: [gfmTableToMarkdown()],
//   });
// }

export interface IRenderContentEvent {
  type: "videoTimeClick";
  data: any;
}

interface IProps {
  sessionId: string;
  content: string;
  webSearch?: IWebSearch[];
  onEvent?: (e: IRenderContentEvent) => void;
  incrementRef?: boolean;
}

const remarkForceTightLists = () => {
  return (tree: any) => {
    visit(tree, "list", node => {
      console.log("list----", tree, node);
      node.spread = true;
    });
  };
};

// const remarkNumberedHeadings = () => {
//   return (tree: any) => {
//     visit(tree, "listItem", node => {
//       console.log("listItem", tree, node);
//       if (node.children && node.children.length === 1) {
//         const firstChild = node.children[0];

//         if (firstChild.type === "heading" && firstChild.depth === 1) {
//           firstChild.type = "paragraph";
//         }
//       }
//     });
//   };
// };

// 自定义 remark 插件处理波浪线
const remarkStrikethrough = () => {
  return (tree: any) => {
    visit(tree, "text", node => {
      // 匹配双波浪线包裹的内容
      const doubleTildeRegex = /~~([^~]+)~~/g;
      let match;
      let lastIndex = 0;
      let newValue = "";

      while ((match = doubleTildeRegex.exec(node.value)) !== null) {
        // 添加双波浪线之前的内容
        newValue += node.value.slice(lastIndex, match.index);
        // 添加删除线内容
        newValue += `<del>${match[1]}</del>`;
        lastIndex = match.index + match[0].length;
      }

      // 添加剩余内容
      newValue += node.value.slice(lastIndex);

      if (newValue !== node.value) {
        node.type = "html";
        node.value = newValue;
      }
    });
  };
};

const MarkdownTextV2 = (props: IProps) => {
  const { content, onEvent, webSearch, incrementRef = false } = props;

  // 预处理内容，将自定义语法转换为特殊的markdown链接
  const processedContent = useMemo(() => {
    let processed = content;

    //     let processed = `首先，商品标价为200元。

    // 1. 降价20%：
    //    降价后的价格 = 原价 × (1 - 降价百分比)
    //    降价后的价格 = 200 × (1 - 0.20) = 200 × 0.80 = 160元

    // 2. 再涨价25%：
    //    涨价后的价格 = 降价后的价格 × (1 + 涨价百分比)
    //    涨价后的价格 = 160 × (1 + 0.25) = 160 × 1.25 = 200元

    // 因此，经过先降价20%，再涨价25%后，商品的现价是 \\boxed{200} 元。
    // 首先，商品标价为200元。

    // 1. 降价10%：
    //    降价后的价格 = 原价 × (1 - 降价百分比)
    //    降价后的价格 = 200 × (1 - 0.20) = 200 × 0.80 = 160元

    //    `;

    // 处理数学公式格式转换
    // 匹配块级公式情况二,\[ \]之间的内容为公式
    processed = processed.replace(
      /\\\[\s*\n?([\s\S]*?)\n?\s*\\\]/g,
      (match, formula) => {
        return `$$${formula.trim()}$$`;
      },
    );

    // 匹配行内公式情况二,\( \)之间的内容为公式
    processed = processed.replace(/\\\(([\s\S]*?)\\\)/g, (match, formula) => {
      return `$${formula}$`;
    });

    // 将 [ref_数字] 转换为特殊链接格式
    processed = processed.replace(/\[ref_(\d+)\]/g, "[ref_$1](https://ref/$1)");

    // 将时间戳格式转换为特殊链接
    processed = processed.replace(
      /\[(\d{2}:\d{2})-(\d{2}:\d{2})\]/g,
      "[$1-$2](https://timestamp/$1-$2)",
    );

    return processed;
  }, [content]);

  const components = useMemo(
    () => ({
      code: CodeBlock,
      img: ({ src, alt, ...props }: any) => (
        <Image
          src={src}
          alt={alt}
          className={styles.markdownImage}
          preview={true}
          {...props}
        />
      ),
      // 处理特殊链接
      a: ({ href, children, ...props }: any) => {
        // 处理自定义ref链接
        if (href && href.startsWith("https://ref/")) {
          const index = parseInt(href.replace("https://ref/", ""), 10);
          // 如果开启incrementRef，显示的数字+1
          const displayIndex = incrementRef ? index + 1 : index;
          return <MyRef index={displayIndex} webSearch={webSearch} />;
        }

        // 处理时间戳链接
        if (href && href.startsWith("https://timestamp/")) {
          const time = href.replace("https://timestamp/", "");
          return <TimeStamp time={time} onEvent={onEvent} />;
        }

        // 普通链接
        return (
          <a href={href} {...props}>
            {children}
          </a>
        );
      },
    }),
    [onEvent, webSearch, incrementRef],
  );

  return (
    <div className={styles.markdownTextV2}>
      {/* <AnimatedMarkdown
        key={"animated-markdown"}
        animation="fadeIn"
        animationDuration="0.8s"
        animationTimingFunction="ease-in-out"
        content={processedContent}
        customComponents={components}
        customRemarkPlugins={[
          remarkMath,
          [remarkGfmTable, { singleTilde: false }],
          remarkStrikethrough,
          remarkNumberedHeadings,
        ]}
        customRehypePlugins={[rehypeKatex, rehypeRaw]}
      /> */}

      {/* <AnimatedMarkdown content={processedContent} /> */}
      <ReactMarkdown
        remarkPlugins={[
          remarkMath,
          [remarkGfm, { singleTilde: false }],
          remarkStrikethrough,
          remarkForceTightLists,
          // remarkNumberedHeadings,
        ]}
        rehypePlugins={[rehypeKatex, rehypeRaw, animatedPlugin]}
        components={components}
        skipHtml={false}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownTextV2;
